// Sensors — the devices that REPORT instead of being controlled.
//
// A door sensor has no outputs. There is no relay to tap, no dimmer, no fan
// step: the whole device is one input, and the only thing the app can say about
// it is which of two states it is in. Drawing it as a device card that opens a
// control screen promises a control that does not exist.
//
// ── What counts as a sensor ────────────────────────────────────────────────
// `Outputs === 0` ALONE IS WRONG, and the estate proves it. Read off the live
// server (user 1, all locations, 2026-08-06):
//
//   DeviceID  name                 Outputs Inputs  Status
//   4         DOOR SENSOR                0      1  [14907604]Closed
//   4         SMARTIFY_804460            0      1  1-0
//   26        Safe Lock Sensor           0      1  Opened          <- no [uno]
//   2         MOTION SENSOR              0      1  1-0
//   7         WATER FLOW SENSOR          0      1  [1110498]No flow detected
//   89        SMARTIFY_804527            0      1  [804527]Shutter Opened BT - LOW
//   23        OFFICE WWLRGB              0      3  [803467]0,0,0,0,0   <- a LIGHT
//   77        CLOCK                      0      0  [1027335]1-10       <- not a sensor
//
// DeviceID 23 is a WWRGB light with five channels and a real control screen, and
// it carries `Outputs 0` on the install row. Zero outputs also covers three IR
// blasters (19, 57, 62), a hub, a clock and a speed tester — none of which have
// two states to report.
//
// ⚠️ SO THE ANSWER COMES FROM THE BACKEND'S OWN `DeviceType` COLUMN, not from a
// guess about the shape. The DeviceTypes table classifies every product —
// 'Sensor', 'Switch', 'Curtain', 'ColorController', 'SmartLock' — and the 31
// zero-output types split cleanly along it (docs/10-device-types.md):
//
//   'Sensor'           2 4 7 8 9 15 16 26 30 36 47 54 64 66 82 89     <- these
//   'Sensor'           67 69 (door sensor variants)                   <- these
//   (blank)            73 74 — Rf Door / Rf Shutter Sensor            <- these
//   'Switch'           19 32 34 57 62 63 72 76 77   blasters, hub, clock
//   'ColorController'  23                            the WWRGB light
//
// ⚠️ AND THE COLUMN IS NOT ON EVERY REPLY. The room list carries `Outputs` and
// `Inputs` but neither `DeviceType` nor `On_off`; the favourites list carries
// `DeviceType` and `On_off` but NEITHER count. So the DeviceID lists below are
// the primary test — an id is on every reply there is — with the columns used
// only to place a type the app has not been told about yet.
//
// ⚠️ THE TWO CURRENT SENSORS (16, 82) KEEP THEIR METER SCREEN. They are
// classified 'Sensor' and have no outputs, but they report volts and amps, not
// one of two states; a green disc would replace real numbers with less than
// nothing. `deviceTypeKey` routes them to PhaseScreen, and the rule below
// requires a type with NO screen of its own — which is what leaves them alone.
//
// ⚠️ THE DOOR LOCK (DeviceID 3) IS NOT A SENSOR EITHER. `DeviceType 'SmartLock'`,
// `Outputs 1`, `On_off 1` — it takes OPENDOOR (102: LOCKUNLOCK), so it keeps its keypad. Anything
// with an output to drive is excluded before any of this is consulted.
//
// ── The two states ─────────────────────────────────────────────────────────
// Green is the resting state, red is the one worth walking over to look at. The
// vocabulary above is four different shapes, so the parse is by SHAPE, not by a
// list of literals:
//
//   `1-0` / `1-1` / `0` / `1`   numeric — 0 rests, non-zero alerts
//   "...closed..."              rests   (the Flutter rule, doorSensor.dart)
//   "no flow detected"          rests   — a NEGATED detection, checked BEFORE
//                                         the word it negates
//   "...open..." / "detected"   alerts
//   anything else non-blank     alerts  — Flutter's `else` branch, kept: for a
//                                         door, an unreadable state is not a
//                                         reason to paint reassurance
//   blank                       unknown — NOT green. Nothing has been heard yet;
//                                         saying "closed" would be inventing it.
//
// The label is the device's own words where it has any ("No flow detected",
// "Shutter Opened BT - LOW"), because they say more than a translation would.
// Only the numeric shapes get a supplied label.

import { deviceTypeKey } from './deviceMeta';

export const SENSOR_NORMAL = 'normal';
export const SENSOR_ALERT = 'alert';
export const SENSOR_UNKNOWN = 'unknown';
// A number that is not 0 or 1 — a temperature, a tank level. Not two-state, so
// it is neither red nor green: the value is shown as it was reported. Painting
// 28 °C red because it is "non-zero" would be worse than saying nothing.
export const SENSOR_READING = 'reading';

// The door family, for the numeric shape's wording. Straight out of the
// DeviceTypes table (docs/10-device-types.md) — every type whose name is a door
// or shutter sensor. A sensor that is not one of these gets neutral words.
const DOOR_SENSOR_IDS = [4, 8, 43, 45, 46, 67, 69, 73, 74, 89];

/**
 * Every zero-output type the backend calls a sensor, by DeviceID.
 *
 * Includes 16 and 82 (the current sensors) for completeness — they ARE sensors —
 * but they own a meter screen, and `isSensorDevice` requires a type with none,
 * so they never take the two-state treatment. Listing them here keeps this the
 * honest copy of the table rather than a filtered one.
 */
export const SENSOR_DEVICE_IDS = [
  2,   // Motion Sensor
  4,   // Door Sensor
  7,   // Water Flow Sensor
  8,   // Gsm Shutter Sensor
  9,   // Finger Print Module
  15,  // Water Level Controller
  16,  // Three Phase Current Sensor      (meter — keeps PhaseScreen)
  26,  // Safe Lock Sensor
  30,  // Temperature Sensor
  36,  // Wifi Remote Buttons
  47,  // Rf Receiver
  54,  // Glass Sensor
  64,  // Microwave Sensor
  66,  // Finger Print Lock Internal Chip
  67,  // Door Sensor With 85
  69,  // Door Sensor With Battery
  73,  // Rf Door Sensor       (DeviceType blank on the row — named as one)
  74,  // Rf Shutter Sensor    (same)
  82,  // Single Phase Current Sensor     (meter — keeps PhaseScreen)
  89,  // Shutter Sensor
];

/**
 * Zero-output types that are NOT sensors, and would otherwise be taken for one.
 * Every one of these has something to send: an IR/AC blaster transmits codes, a
 * hub relays, a clock displays. None has two states to paint.
 */
export const NON_SENSOR_ZERO_OUTPUT_IDS = [
  19,  // Ir Blaster                 (On_off 1)
  23,  // Wwhrgb Dimmable Controller (a LIGHT — 5 channels, own screen)
  32,  // Wifi Speed Tester
  34,  // Cupboard Switch
  57,  // Ir Receiver Transmiter     (On_off 1)
  62,  // Universal Ac Blaster       (On_off 1)
  63,  // Data Transmit
  72,  // Hub
  75,  // Rf Switch
  76,  // Smartifymaster
  77,  // Clock
  101, // 4 Scene Controller          (runs the room's scenes — own screen)
];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Is this row a sensor — something that reports rather than something to drive?
 *
 * ⚠️ Conservative on purpose. A row that cannot prove it is a sensor stays an
 * ordinary device card, which is the behaviour that was there before. Guessing
 * the other way empties a grid.
 */
export function isSensorDevice(row) {
  if (!row) return false;
  const id = Number(row.DeviceID ?? row.DeviceId);
  const outputs = num(row.Outputs ?? row.outputs);

  // Something to drive is not a sensor, whatever it is called. This is what
  // keeps the door lock (3, one output) and the sensors that ship WITH an output
  // (43, 45, 46 — hooter, washroom light) on their own controls.
  if (outputs != null && outputs > 0) return false;

  // A type with a screen of its own keeps it — meters included.
  if (deviceTypeKey(id) !== 'unsupported') return false;

  if (SENSOR_DEVICE_IDS.includes(id)) return true;
  if (NON_SENSOR_ZERO_OUTPUT_IDS.includes(id)) return false;

  // A DeviceID the app has not been told about. Fall back to what the row says,
  // which is why the two column names are read defensively: `DeviceType` is the
  // backend's classification string and appears on some replies, `On_off` marks
  // a type that TAKES on/off commands and therefore is not a reporter.
  if (String(row.DeviceType ?? '').trim().toLowerCase() === 'sensor') return true;
  if (Number(row.On_off) === 1) return false;
  const inputs = num(row.Inputs ?? row.inputs);
  return outputs === 0 && inputs >= 1;
}

/**
 * A sensor's state and the words for it.
 *
 * -> { state: 'normal' | 'alert' | 'unknown', label }
 *
 * `status` is the parsed status (what mqttClient.parseStatus returns — the part
 * between `]` and `#`), or the raw `Status` column; both are accepted, since the
 * server's column keeps the `[uno]` prefix and the live frame does not.
 */
export function sensorStateOf(status, deviceId) {
  const raw = String(status ?? '').trim();
  // Tolerate the stored shape, `[14907604]Closed`, as well as the wire shape.
  const body = (() => {
    const close = raw.indexOf(']');
    const text = close === -1 ? raw : raw.slice(close + 1);
    const hash = text.indexOf('#');
    return (hash === -1 ? text : text.slice(0, hash)).trim();
  })();

  if (!body) return { state: SENSOR_UNKNOWN, label: 'No report yet' };

  const isDoor = DOOR_SENSOR_IDS.includes(Number(deviceId));

  // `1-0`, `1-1`, `0`, `1` — the shape motion sensors and some door sensors use.
  const dash = /^(?:\d+-)?(\d+)$/.exec(body);
  if (dash) {
    const v = Number(dash[1]);
    // ⚠️ ONLY 0 AND 1 ARE STATES. A temperature sensor (30) and a water level
    // controller (15) are on this list too and report a MEASUREMENT; "non-zero
    // means triggered" would make 28 °C an alarm. Anything else is reported as
    // the number it is.
    if (v > 1) return { state: SENSOR_READING, label: String(v) };
    const on = v === 1;
    if (isDoor) return { state: on ? SENSOR_ALERT : SENSOR_NORMAL, label: on ? 'Opened' : 'Closed' };
    return { state: on ? SENSOR_ALERT : SENSOR_NORMAL, label: on ? 'Detected' : 'Clear' };
  }

  const lower = body.toLowerCase();

  // ⚠️ A CONNECTIVITY WORD IS NOT AN ALARM, and the last line of this function
  // used to make it one. The RF receiver (47) stores `Status = 'Online'` —
  // written by the firmware about the LINK, not about anything it senses — so
  // it is not a number, is not negated and does not say "closed", and fell
  // through to SENSOR_ALERT: a working receiver drew a full red disc labelled
  // ONLINE in the customer's room (reported 2026-10-03). Liveness has its own
  // dot on every tile; it must never also be the state.
  if (/^(online|offline|connected|disconnected|connecting|ok|alive)$/.test(lower)) {
    return { state: SENSOR_UNKNOWN, label: body };
  }
  // A negated detection is still a resting state, and it CONTAINS the word that
  // would otherwise trip the alert branch — so it is tested first.
  const negated = /\bno\b[^.]*\b(flow|motion|detect\w*|leak|smoke|water)\b/.test(lower)
    || lower.includes('not detected')
    || lower.includes('normal');
  if (negated || lower.includes('closed')) {
    return { state: SENSOR_NORMAL, label: body };
  }
  return { state: SENSOR_ALERT, label: body };
}

/** The two rows a screen draws: sensors, and everything else, order preserved. */
export function splitSensors(devices) {
  const list = Array.isArray(devices) ? devices : [];
  return {
    sensors: list.filter(isSensorDevice),
    controls: list.filter((d) => !isSensorDevice(d)),
  };
}

// ── A sensor that has gone silent (2026-10-19) ─────────────────────────────
// A door / shutter / motion sensor reports only on a CHANGE, so an old state
// can still be true ("closed since yesterday"). What makes it untrustworthy is
// the DEVICE going silent: 804730 (Shutter Sensor) showed a green "ONLINE" disc
// fourteen days after its last heartbeat. Past SENSOR_SILENT_MS without a
// heartbeat, and with nothing heard live, the card shows the state as LAST
// KNOWN — greyed, with its age — never as now.
export const SENSOR_SILENT_MS = 2 * 60 * 60 * 1000;

/**
 * -> ms since the sensor was last heard, when that is over SENSOR_SILENT_MS;
 *    null when it is current (or nothing is known).
 * `ageOfStamp` is deviceStatus.ageOf (server stamps run 5.5 h ahead);
 * `liveAt` is when this app last heard it over MQTT (our clock), or null.
 */
export function sensorSilence(device, liveAt, parseStamp, ageOfStamp, now = Date.now()) {
  if (liveAt && now - liveAt < SENSOR_SILENT_MS) return null;
  const raw = device && (device.lastUpdatedDateTime ?? device.LastUpdatedDateTime);
  const ms = parseStamp(raw);
  if (ms == null) return null;
  const age = ageOfStamp(ms);
  return age > SENSOR_SILENT_MS ? age : null;
}

/** "14 days" / "5 h" — for the silent-sensor caption. */
export function silenceText(ms) {
  const h = ms / 3600000;
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} days`;
}
